import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { IzzyMetadataEngine, IzzyChannelExtensionEntity } from 'mj_generatedentities';
import { BCSaaSChannelEntity, BCSaaSChannelTypeEntity } from '@bluecypress/bcsaas-entities';
import { BCSaaSOrganizationCredentialEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { MJCredentialEntity } from '@memberjunction/core-entities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';
import { DisplayCredentialName } from '../../utils/org-credentials';

interface ChannelDisplay {
  channel: BCSaaSChannelEntity;
  channelType: BCSaaSChannelTypeEntity | null;
  organization: mjBizAppsCommonOrganizationEntity | null;
  // Post-BAC: ActionInheritMode / CredentialID / AIConfigurationID live on Izzy.ChannelExtension,
  // not on the BCSaaS Channel row. The display reads these off the extension (1:1 by ChannelID).
  extension: IzzyChannelExtensionEntity | null;
  isEditing: boolean;
  editForm: ChannelEditForm;
}

interface ChannelEditForm {
  name: string;
  description: string;
  organizationId: string;
  channelTypeId: string;
  externalIdentifier: string;
  checkFrequency: number;
  actionInheritMode: 'Organization' | 'ChannelType' | 'Channel' | 'Combined';
  credentialId: string | null;
  status: 'Active' | 'Pending' | 'Revoked';
}

@Component({
  standalone: false,
  selector: 'izzy-channels',
  templateUrl: './channels.component.html',
  styleUrls: ['./channels.component.css']
})
export class ChannelsComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {
  /** Credential name without BCSaaS's ` [organizationID]` suffix (template helper). */
  protected readonly displayCredentialName = DisplayCredentialName;

  @Input() organizationId: string | undefined;
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected channels: BCSaaSChannelEntity[] = [];
  protected channelDisplays: ChannelDisplay[] = [];
  protected channelTypes: BCSaaSChannelTypeEntity[] = [];
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected credentials: MJCredentialEntity[] = [];
  // Post-BAC: ActionInheritMode, AIConfigurationID, CredentialID moved off BCSaaS.Channel into Izzy.ChannelExtension.
  // We cache extensions alongside channels so the form can read+write them on edit.
  protected channelExtensions: IzzyChannelExtensionEntity[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';
  protected statusFilter: string = 'all';
  protected orgFilter: string = 'all';

  protected isCreating: boolean = false;
  protected createForm: ChannelEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to organizationId input changes
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      this.loadData();
    }
  }

  private getEmptyForm(): ChannelEditForm {
    return {
      name: '',
      description: '',
      organizationId: this.organizationId || '',
      channelTypeId: '',
      externalIdentifier: '',
      checkFrequency: 5,
      actionInheritMode: 'ChannelType',
      credentialId: null,
      status: 'Active'
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      await IzzyMetadataEngine.Instance.Config(false);
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Load channel types
      const rvTypes = new RunView();
      const typesResult = await rvTypes.RunView<BCSaaSChannelTypeEntity>({
        EntityName: 'BC: Channel Types',
        ExtraFilter: "Status = 'Active'",
        OrderBy: 'Name ASC',
        ResultType: 'entity_object'
      });
      this.channelTypes = typesResult.Results;

      // Load channels
      let extraFilter = '';
      if (this.organizationId) {
        extraFilter = `OrganizationID = '${this.organizationId}'`;
      }
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSChannelEntity>({
        EntityName: 'BC: Channels',
        ExtraFilter: extraFilter || undefined,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object'
      });
      this.channels = result.Results;

      // Load Izzy.ChannelExtension rows for the loaded channels (1:1 by ChannelID)
      if (this.channels.length > 0) {
        const channelIds = this.channels.map(c => `'${c.ID}'`).join(',');
        const rvExt = new RunView();
        const extResult = await rvExt.RunView<IzzyChannelExtensionEntity>({
          EntityName: 'Channel Extensions',
          ExtraFilter: `ChannelID IN (${channelIds})`,
          ResultType: 'entity_object'
        });
        this.channelExtensions = extResult.Success ? extResult.Results : [];
      } else {
        this.channelExtensions = [];
      }

      this.channelDisplays = this.channels.map(c => this.createDisplay(c));

      // Load credentials
      await this.loadCredentials();

      // Emit status after data is loaded
      this.emitPanelStatus();
    } catch (error) {
      console.error('Error loading channels:', error);
      this.errorMessage = 'Failed to load channels.';
    } finally {
      this.isLoading = false;
    }
  }

  private createDisplay(channel: BCSaaSChannelEntity): ChannelDisplay {
    const channelType = this.channelTypes.find(ct => ct.ID === channel.ChannelTypeID) || null;
    const organization = this.organizations.find(o => o.ID === channel.OrganizationID) || null;
    return {
      channel,
      channelType,
      organization,
      extension: this.getExtensionForChannel(channel.ID),
      isEditing: false,
      editForm: this.channelToForm(channel)
    };
  }

  private orgCredentialLinks: BCSaaSOrganizationCredentialEntity[] = [];

  private async loadCredentials(): Promise<void> {
    try {
      const rv = new RunView();

      // Load org-credential bridge records
      const linksResult = await rv.RunView<BCSaaSOrganizationCredentialEntity>({
        EntityName: 'BC: Organization Credentials',
        ExtraFilter: "Status = 'Active'",
        ResultType: 'entity_object'
      });
      this.orgCredentialLinks = linksResult.Success ? linksResult.Results : [];

      // Load all active credentials
      const result = await rv.RunView<MJCredentialEntity>({
        EntityName: 'MJ: Credentials',
        ExtraFilter: "IsActive = 1",
        OrderBy: 'Name ASC'
      });
      this.credentials = result.Success ? result.Results : [];
    } catch (error) {
      console.error('Error loading credentials:', error);
      this.credentials = [];
      this.orgCredentialLinks = [];
    }
  }

  protected getCredentialName(credentialId: string | null | undefined): string {
    if (!credentialId) return '';
    const credential = this.credentials.find(c => c.ID === credentialId);
    return credential?.Name || 'Unknown';
  }

  protected getCredentialsForOrganization(organizationId: string): MJCredentialEntity[] {
    const credentialIds = new Set(
      this.orgCredentialLinks
        .filter(l => l.OrganizationID === organizationId)
        .map(l => l.CredentialID)
    );
    return this.credentials.filter(c => credentialIds.has(c.ID));
  }

  /** Sync lookup for the channel's extension row (post-BAC). */
  private getExtensionForChannel(channelId: string): IzzyChannelExtensionEntity | null {
    return this.channelExtensions.find(e => e.ChannelID === channelId) || null;
  }

  private channelToForm(channel: BCSaaSChannelEntity): ChannelEditForm {
    const ext = this.getExtensionForChannel(channel.ID);
    return {
      name: channel.Name,
      description: channel.Description || '',
      organizationId: channel.OrganizationID,
      channelTypeId: channel.ChannelTypeID,
      externalIdentifier: channel.ExternalIdentifier || '',
      checkFrequency: channel.CheckFrequency || 5,
      actionInheritMode: (ext?.ActionInheritMode as ChannelEditForm['actionInheritMode']) || 'ChannelType',
      credentialId: ext?.CredentialID || null,
      status: channel.Status as 'Active' | 'Pending' | 'Revoked'
    };
  }

  /**
   * Save (or create) the Izzy.ChannelExtension row for a channel with the form values for
   * ActionInheritMode / CredentialID. Updates the local cache so subsequent reads see the new values.
   */
  private async saveExtensionForChannel(
    channelId: string,
    form: Pick<ChannelEditForm, 'actionInheritMode' | 'credentialId'>
  ): Promise<boolean> {
    const md = new Metadata();
    let ext = this.getExtensionForChannel(channelId);
    let isNew = false;
    if (!ext) {
      ext = await md.GetEntityObject<IzzyChannelExtensionEntity>('Channel Extensions');
      ext.ChannelID = channelId;
      isNew = true;
    }
    ext.ActionInheritMode = form.actionInheritMode;
    ext.CredentialID = form.credentialId;
    const ok = await ext.Save();
    if (!ok) {
      console.error('Failed to save ChannelExtension:', ext.LatestResult);
      return false;
    }
    if (isNew) {
      this.channelExtensions.push(ext);
    }
    return true;
  }

  get filteredChannels(): ChannelDisplay[] {
    return this.channelDisplays.filter(cd => {
      const matchesSearch = !this.searchQuery.trim() ||
        cd.channel.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (cd.channel.ExternalIdentifier && cd.channel.ExternalIdentifier.toLowerCase().includes(this.searchQuery.toLowerCase()));
      const matchesStatus = this.statusFilter === 'all' || cd.channel.Status === this.statusFilter;
      const matchesOrg = this.orgFilter === 'all' || cd.channel.OrganizationID === this.orgFilter;
      return matchesSearch && matchesStatus && matchesOrg;
    });
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    if (this.organizations.length > 0 && !this.createForm.organizationId) {
      this.createForm.organizationId = this.organizations[0].ID;
    }
    if (this.channelTypes.length > 0) {
      this.createForm.channelTypeId = this.channelTypes[0].ID;
    }
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Channel name is required.';
      return;
    }
    if (!this.createForm.organizationId || !this.createForm.channelTypeId) {
      this.errorMessage = 'Organization and channel type are required.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      // Post-BAC, creating a channel writes TWO rows: __BCSaaS.Channel (base) and
      // Izzy.ChannelExtension (1:1 child, FK'd to the base). These must commit atomically —
      // a plain back-to-back Save() of each races: the extension insert can reach the DB
      // before the base row is committed/visible, failing FK_ChannelExtension_Channel.
      // A TransactionGroup defers both saves and submits them as one transaction.
      // NewRecord() assigns the client-side UUID up front so the extension can reference
      // the channel ID before the base row persists.
      const tg = await md.CreateTransactionGroup();

      const newChannel = await md.GetEntityObject<BCSaaSChannelEntity>('BC: Channels');
      newChannel.NewRecord();
      newChannel.Name = this.createForm.name.trim();
      newChannel.Description = this.createForm.description.trim() || null;
      newChannel.OrganizationID = this.createForm.organizationId;
      newChannel.ChannelTypeID = this.createForm.channelTypeId;
      newChannel.ExternalIdentifier = this.createForm.externalIdentifier.trim() || null;
      newChannel.CheckFrequency = this.createForm.checkFrequency;
      newChannel.Status = this.createForm.status;
      newChannel.TransactionGroup = tg;
      await newChannel.Save();

      const ext = await md.GetEntityObject<IzzyChannelExtensionEntity>('Channel Extensions');
      ext.NewRecord();
      ext.ChannelID = newChannel.ID;
      ext.ActionInheritMode = this.createForm.actionInheritMode;
      ext.CredentialID = this.createForm.credentialId;
      ext.TransactionGroup = tg;
      await ext.Save();

      if (!await tg.Submit()) {
        console.error('Failed to create channel:', newChannel.LatestResult, ext.LatestResult);
        this.errorMessage = newChannel.LatestResult?.CompleteMessage || 'Failed to create channel.';
        return;
      }

      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Channel created');
    } catch (error) {
      console.error('Error creating channel:', error);
      this.errorMessage = 'Failed to create channel.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: ChannelDisplay): void {
    display.isEditing = true;
    display.editForm = this.channelToForm(display.channel);
  }

  cancelEdit(display: ChannelDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: ChannelDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.channel.Name = display.editForm.name.trim();
      display.channel.Description = display.editForm.description.trim() || null;
      display.channel.OrganizationID = display.editForm.organizationId;
      display.channel.ChannelTypeID = display.editForm.channelTypeId;
      display.channel.ExternalIdentifier = display.editForm.externalIdentifier.trim() || null;
      display.channel.CheckFrequency = display.editForm.checkFrequency;
      display.channel.Status = display.editForm.status;

      const saveResult = await display.channel.Save();
      if (!saveResult) {
        console.error('Failed to update channel:', display.channel.LatestResult);
        this.errorMessage = display.channel.LatestResult?.CompleteMessage || 'Failed to update channel.';
        return;
      }

      // Save the ChannelExtension row (post-BAC: holds ActionInheritMode + CredentialID)
      const extOk = await this.saveExtensionForChannel(display.channel.ID, display.editForm);
      if (!extOk) {
        this.errorMessage = 'Channel updated but extension save failed; ActionInheritMode/CredentialID may not be saved.';
      }

      display.channelType = this.channelTypes.find(ct => ct.ID === display.channel.ChannelTypeID) || null;
      display.organization = this.organizations.find(o => o.ID === display.channel.OrganizationID) || null;
      display.isEditing = false;
      this.showSuccess('Channel updated');
    } catch (error) {
      console.error('Error updating channel:', error);
      this.errorMessage = 'Failed to update channel.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(display: ChannelDisplay): void {
    this.showConfirmDialog(
      'Delete Channel',
      `Are you sure you want to delete "${display.channel.Name}"? All associated messages and runs will also be deleted.`,
      async () => {
        this.isSaving = true;
        try {
          const deleteResult = await display.channel.Delete();
          if (!deleteResult) {
            console.error('Failed to delete channel:', display.channel.LatestResult);
            this.errorMessage = display.channel.LatestResult?.CompleteMessage || 'Failed to delete channel.';
            return;
          }
          await this.loadData();
          this.showSuccess('Channel deleted');
        } catch (error) {
          console.error('Error deleting channel:', error);
          this.errorMessage = 'Failed to delete channel.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Revoked': return 'status-revoked';
      default: return '';
    }
  }

  getInheritModeLabel(mode: string | null | undefined): string {
    switch (mode) {
      case 'Organization': return 'Organization Only';
      case 'ChannelType': return 'Channel Type';
      case 'Channel': return 'Channel Only';
      case 'Combined': return 'Combined';
      // No extension row / unset → fall back to the system default mode label.
      default: return mode ? mode : 'Channel Type';
    }
  }

  getChannelTypeIcon(typeName: string): string {
    const name = (typeName || '').toLowerCase();
    if (name.includes('email')) return 'fa-envelope';
    if (name.includes('slack')) return 'fa-slack';
    if (name.includes('teams')) return 'fa-microsoft';
    if (name.includes('sms')) return 'fa-sms';
    if (name.includes('whatsapp')) return 'fa-whatsapp';
    if (name.includes('chat')) return 'fa-comments';
    return 'fa-broadcast-tower';
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }

  // ========== Admin Panel Status ==========

  private emitPanelStatus(): void {
    const status = this.calculatePanelStatus();
    this.statusChange.emit(status);
  }

  private calculatePanelStatus(): AdminPanelStatus {
    const totalChannels = this.channelDisplays.length;
    const activeChannels = this.channelDisplays.filter(cd => cd.channel.Status === 'Active').length;
    const pendingChannels = this.channelDisplays.filter(cd => cd.channel.Status === 'Pending').length;
    const revokedChannels = this.channelDisplays.filter(cd => cd.channel.Status === 'Revoked').length;

    if (totalChannels === 0) {
      return {
        status: 'warning',
        tooltip: 'No channels configured'
      };
    }

    if (revokedChannels > 0) {
      return {
        status: 'warning',
        tooltip: `${revokedChannels} channel(s) revoked`
      };
    }

    if (pendingChannels > 0 && activeChannels === 0) {
      return {
        status: 'warning',
        tooltip: `${pendingChannels} channel(s) pending, none active`
      };
    }

    return {
      status: 'good',
      tooltip: `${activeChannels} active channel(s)`
    };
  }
}

export function LoadChannelsComponent(): void {}
